import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { Context } from '@grandvisionhq/carts-v2';
import { toast } from '@grandvisionhq/common';
import { useLink } from '@grandvisionhq/frk-routing';
import { useSync } from '@grandvisionhq/lib-utils-w3c';
import { WithUserData } from '@grandvisionhq/my-account';
import { useIntl, useDispatch, useSelector, useFeatureToggles } from '@grandvisionhq/state';
import { init } from '../actions.js';
import { useCheckoutSettings } from '../context/CheckoutSettings/context.js';
import '../models/cart/cart-state.js';
import '../models/checkout/checkout-step-error-type.js';
import '../models/checkout-settings.js';
import '../models/step-shipping/shipping-methods.js';
import { StepNames } from '../models/shared.js';
import { resetMyAccountValidation } from './actions.js';
import { useFormPrefill } from './hooks/use-form-prefill.js';
import { useInvalidShippingStep } from './hooks/use-invalid-shipping-step.js';
import { useShippingStepValidation } from './hooks/use-shipping-step-validation.js';
import { useUpdatedShippingMethods } from './hooks/use-updated-shipping-methods.js';
import { isPrefillFinished } from './selectors.js';
import { StepShipping } from './StepShipping.js';

const StepShippingContainer = ({ onNextStep , onPrevStep , shippingMethodsAsides , userData ,  })=>{
    var ref, ref1;
    const checkoutSettings = useCheckoutSettings();
    useFormPrefill(userData);
    const { getLabel  } = useIntl();
    const dispatch = useDispatch();
    const { loading: isCartLoading  } = Context.useCart();
    const isPrefillFinished$1 = useSelector(isPrefillFinished);
    const [isOptInsFetching, setIsOptInsFetching] = React.useState(true);
    const [isServiceStoreUpdating, setIsServiceStoreUpdating] = React.useState(true);
    const shouldShowLoader = React.useMemo(()=>(isOptInsFetching || !isPrefillFinished$1) && !isCartLoading
    , [
        isCartLoading,
        isOptInsFetching,
        isPrefillFinished$1
    ]);
    var ref2;
    const isFirstStep = React.useMemo(()=>{
        return Object.keys((ref2 = checkoutSettings === null || checkoutSettings === void 0 ? void 0 : checkoutSettings.routes) !== null && ref2 !== void 0 ? ref2 : {})[0] === StepNames.Shipping;
    }, [
        checkoutSettings === null || checkoutSettings === void 0 ? void 0 : checkoutSettings.routes
    ]);
    const isCreateAccountRequired = Boolean(((ref = checkoutSettings.account) === null || ref === void 0 ? void 0 : ref.implicitAccountCreation) || ((ref1 = checkoutSettings.account) === null || ref1 === void 0 ? void 0 : ref1.createAccountRequired));
    const { onClick: navigateToCart  } = useLink({
        route: 'cart.overview'
    }, {
        replace: true
    }) || {};
    const broadCastChannel = useSync('new-cart-created').getChannel();
    broadCastChannel.onmessage = ()=>{
        toast.failed('cart.errors.cartNotInActiveState');
        navigateToCart === null || navigateToCart === void 0 ? void 0 : navigateToCart();
    };
    const { isStepShippingValid  } = useShippingStepValidation();
    const { handleInvalidStepShipping  } = useInvalidShippingStep();
    const { isShippingMethodStateValid , handleUpdateShippingMethodsState , shouldShowUpdatedShippingMethodsModal , onCloseUpdatedShippingMethodsModal ,  } = useUpdatedShippingMethods({
        onNextStep,
        isServiceStoreUpdating
    });
    React.useEffect(()=>{
        if (!isCreateAccountRequired) {
            dispatch(resetMyAccountValidation());
        }
    // This should be called only when createAccountRequired flag changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isCreateAccountRequired
    ]);
    React.useEffect(()=>{
        dispatch(init());
    }, [
        dispatch
    ]);
    /**
   *  Callback from OptInContainer to return
   *  loading state of OptIns from GraphQL call
   *
   *  @param isOptInsLoading
   *    flag to return loading state of OptIns
   */ const handleOptInsLoading = React.useCallback(({ isOptInsLoading  })=>{
        setIsOptInsFetching(isOptInsLoading);
    }, []);
    /**
   *  Callback from ServiceStoreContainer to return
   *  loading state of ServiceStore from GraphQL call
   *
   *  @param isServiceStoreLoading
   *    flag to return loading state of ServiceStore
   */ const handleServiceStoreLoading = React.useCallback(({ isServiceStoreLoading  })=>{
        setIsServiceStoreUpdating(isServiceStoreLoading);
    }, []);
    const handleNextStep = React.useCallback((bypassValidation)=>{
        if (!isStepShippingValid() && !bypassValidation) {
            handleInvalidStepShipping();
        } else if (!isShippingMethodStateValid() && !bypassValidation) {
            handleUpdateShippingMethodsState();
            return;
        } else {
            onNextStep();
        }
    }, [
        handleUpdateShippingMethodsState,
        handleInvalidStepShipping,
        isShippingMethodStateValid,
        isStepShippingValid,
        onNextStep, 
    ]);
    const labels = React.useMemo(()=>({
            generic: {
                title: getLabel('checkout.generic.title')
            },
            customerType: {
                private: getLabel('checkout.customerType.private'),
                company: getLabel('checkout.customerType.company')
            },
            shipmentMethodTitle: getLabel('checkout.shipment.method.title'),
            serviceStoreTitle: getLabel('checkout.serviceStore.title'),
            serviceStoreSubtitle: getLabel('checkout.serviceStore.subtitle'),
            alternativeShippingMethodTitle: getLabel('checkout.shipment.method.alternative.modal.title')
        })
    , [
        getLabel
    ]);
    return /*#__PURE__*/ React.createElement(StepShipping, {
        labels: labels,
        onNextStep: handleNextStep,
        onPrevStep: onPrevStep,
        isFirstStep: isFirstStep,
        shippingMethodsAsides: shippingMethodsAsides,
        shouldShowLoader: shouldShowLoader,
        handleOptInsLoading: handleOptInsLoading,
        handleServiceStoreLoading: handleServiceStoreLoading,
        shouldShowUpdatedShippingMethodsModal: shouldShowUpdatedShippingMethodsModal,
        onCloseUpdatedShippingMethodsModal: onCloseUpdatedShippingMethodsModal
    });
};
const StepShippingContainerWithUserData = WithUserData(StepShippingContainer);
const StepShippingContainerWithMyAccount = (_param)=>{
    var props = swcHelpers.extends({}, _param);
    const { isMyAccountEnabled  } = useFeatureToggles();
    return /*#__PURE__*/ React.createElement(StepShippingContainerWithUserData, swcHelpers.extends({}, props, {
        isMyAccountEnabled: isMyAccountEnabled
    }));
};

export { StepShippingContainerWithMyAccount as default };
//# sourceMappingURL=StepShippingContainer.js.map
